import { useEffect, useRef, useSyncExternalStore } from "react";
import type { CuadraChatClient } from "../lib/cuadraChatClient";
import { artifactStore } from "../adapters/artifactStore";
import { backgroundGenerationStore } from "../adapters/backgroundGenerationStore";
import {
  type ArtifactListItem,
  isDocumentArtifactGenerating,
} from "../types/artifact";

/** API list order is not guaranteed; prefer highest version, then newest createdAt. */
function pickLatestListItem(
  items: ArtifactListItem[],
): ArtifactListItem | undefined {
  if (!items.length) return undefined;
  return items.reduce((best, cur) => {
    if (cur.version !== best.version) {
      return cur.version > best.version ? cur : best;
    }
    const ct = Date.parse(cur.createdAt);
    const bt = Date.parse(best.createdAt);
    const c = Number.isFinite(ct) ? ct : 0;
    const b = Number.isFinite(bt) ? bt : 0;
    return c >= b ? cur : best;
  });
}

interface UseArtifactLoaderOptions {
  client: CuadraChatClient | null | undefined;
  chatId: string | null | undefined;
  enableArtifacts: boolean;
  /** After list load, open this artifact (server id or identifier). */
  initialOpenArtifactId?: string | null;
  /**
   * When set, `chatId` may be transiently null (runtime remount / bg reload)
   * while a server thread still exists — do not clear the artifact store.
   * singleChat: pass the same resolved id as `serverThreadId` on the provider.
   */
  resolvedServerThreadId?: string | null | undefined;
  onArtifactComplete?: (chatId: string, artifactId?: string) => void;
}

function openArtifactFromToken(token: string, items: ArtifactListItem[]): void {
  const t = token.trim();
  if (!t) return;
  const match = items.find((i) => i.id === t || i.identifier === t);
  artifactStore.setActiveArtifact(match?.id ?? t);
}

/**
 * Loads existing artifacts when opening a chat that previously had artifacts.
 * Resolves pending artifact identifier when user opens panel by identifier (e.g. from message-stream marker).
 * Also provides a refresh function to call after a stream completes.
 */
export function useArtifactLoader({
  client,
  chatId,
  enableArtifacts,
  initialOpenArtifactId,
  resolvedServerThreadId,
  onArtifactComplete,
}: UseArtifactLoaderOptions) {
  const loadedChatIdRef = useRef<string | null>(null);
  /** Last server chat id this loader ran for — used to clear store only on real thread switches. */
  const loaderChatIdRef = useRef<string | null>(null);
  const lastListFetchKeyRef = useRef<string | null>(null);
  const resolvingRef = useRef(false);

  const activeArtifactId = useSyncExternalStore(
    (listener) => artifactStore.subscribe(listener),
    () => artifactStore.getSnapshot().activeArtifactId,
    () => null,
  );

  useEffect(() => {
    if (!client) return;

    if (!enableArtifacts) {
      artifactStore.clearArtifacts();
      loadedChatIdRef.current = null;
      loaderChatIdRef.current = null;
      lastListFetchKeyRef.current = null;
      return;
    }

    // Server id not ready yet (runtime remount, first paint). Do not clear while
    // a resolved server thread still exists (bg-reload latch, URL thread, or
    // runtime latch) — clearing used to break when the thread reloaded after
    // background generation. When the host is on a true blank composer, both
    // `chatId` and `resolvedServerThreadId` are unset: clear the singleton store
    // so another thread's artifact panel cannot linger.
    if (!chatId) {
      lastListFetchKeyRef.current = null;
      const pendingServerThread =
        (resolvedServerThreadId ?? "").trim().length > 0;
      if (!pendingServerThread) {
        artifactStore.clearArtifacts();
        loadedChatIdRef.current = null;
        loaderChatIdRef.current = null;
      }
      return;
    }

    if (chatId.startsWith("__LOCALID_")) {
      artifactStore.clearArtifacts();
      loadedChatIdRef.current = null;
      loaderChatIdRef.current = null;
      lastListFetchKeyRef.current = null;
      return;
    }

    // Only treat as a "switch" when we already had a *different* server chat id.
    // On first mount or remount (e.g. thread `committed.key` bump after stream end),
    // refs reset to null while `chatId` is unchanged — `null !== chatId` must not
    // clear the store, especially because `activeStreamingChatIds` drops the id
    // in the adapter `finally` before children re-render.
    const prevLoaderChatId = loaderChatIdRef.current;
    const switchedChat =
      prevLoaderChatId !== null && prevLoaderChatId !== chatId;
    loaderChatIdRef.current = chatId;

    const want = (initialOpenArtifactId ?? "").trim();
    const fetchKey = `${chatId}|${want}`;
    if (lastListFetchKeyRef.current === fetchKey) return;
    lastListFetchKeyRef.current = fetchKey;
    loadedChatIdRef.current = chatId;

    // Only wipe on a real chat switch (not when only `initialOpenArtifactId` changes).
    // Otherwise a refetch key change after the stream ends clears the panel and the
    // in-flight artifact the user was watching.
    if (switchedChat) {
      artifactStore.clearArtifacts();
    }

    client.listArtifacts(chatId).then(
      (response) => {
        if (lastListFetchKeyRef.current !== fetchKey) return;
        artifactStore.loadArtifacts(response.items);
        if (want) {
          openArtifactFromToken(want, response.items);
        }

        const bg = backgroundGenerationStore.getSnapshot();
        if (bg?.chatId === chatId && bg.status === "polling") {
          void client.getChat(chatId).then((chat) => {
            if (chat.status != null) return;
            if (lastListFetchKeyRef.current !== fetchKey) return;
            backgroundGenerationStore.finishPollingSuccess(chatId);
          });
        }

        if (
          isDocumentArtifactGenerating(
            artifactStore.getSnapshot().artifactUiPhase,
          )
        ) {
          const hasStreaming = [...artifactStore.getSnapshot().artifacts.values()].some(
            (a) => a.status === "streaming",
          );
          if (!hasStreaming) {
            artifactStore.endDocumentGeneration();
          }
        }
      },
      () => {
        // Silently ignore - chat may not have artifacts
      },
    );
  }, [
    client,
    chatId,
    enableArtifacts,
    initialOpenArtifactId,
    resolvedServerThreadId,
  ]);

  // Resolve pending identifier: when panel is "open" on an id that isn't in the store yet, fetch list and load
  useEffect(() => {
    if (
      !client ||
      !enableArtifacts ||
      !chatId ||
      chatId.startsWith("__LOCALID_") ||
      !activeArtifactId ||
      resolvingRef.current
    )
      return;
    if (artifactStore.getActiveArtifact()) return;

    resolvingRef.current = true;
    client
      .listArtifacts(chatId)
      .then((response) => {
        const item = response.items.find(
          (i) => i.identifier === activeArtifactId,
        );
        if (item) {
          artifactStore.loadArtifacts([item]);
          artifactStore.setActiveArtifact(item.id);
        }
      })
      .finally(() => {
        resolvingRef.current = false;
      });
  }, [client, chatId, enableArtifacts, activeArtifactId]);

  const refreshAfterStream = (streamChatId: string) => {
    if (!client || !enableArtifacts) return;

    client.listArtifacts(streamChatId).then(
      (response) => {
        const hadDocPending = isDocumentArtifactGenerating(
          artifactStore.getSnapshot().artifactUiPhase,
        );
        artifactStore.loadArtifacts(response.items);

        if (hadDocPending) {
          const latest = pickLatestListItem(response.items);
          if (latest) {
            artifactStore.setActiveArtifact(latest.id);
          }
          artifactStore.endDocumentGeneration();
        } else {
          const snap = artifactStore.getSnapshot();
          if (snap.panelOpen && response.items.length > 0) {
            const active = artifactStore.getActiveArtifact();
            const ident = active?.identifier;
            if (ident) {
              const newest = [...response.items]
                .reverse()
                .find((i) => i.identifier === ident);
              if (newest && newest.id !== snap.activeArtifactId) {
                artifactStore.setActiveArtifact(newest.id);
              }
            } else if (
              snap.activeArtifactId &&
              !artifactStore.getActiveArtifact()
            ) {
              const latest = pickLatestListItem(response.items);
              if (latest) {
                artifactStore.setActiveArtifact(latest.id);
              }
            }
          }
        }

        if (artifactStore.hasStreamingArtifacts()) {
          artifactStore.refreshVersions(response.items);
        }

        const latest = pickLatestListItem(response.items);
        if (latest) {
          onArtifactComplete?.(streamChatId, latest.id);
        }
      },
      () => {
        // Silently ignore
      },
    );
  };

  return { refreshAfterStream };
}
